import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, FileText, Upload, GitCompare, Package, 
  Users, Settings, LogOut, Menu, X, Bell, ChevronRight, ChevronLeft,
  Inbox, BarChart3, User, Save, Calendar, Clock, Coins, Info, AlertCircle,
  Shield, Eye, EyeOff, Trash2, CheckCircle, XCircle, FileCheck, Download,
  AlertTriangle, RefreshCw, Link2, Unlink
} from "lucide-react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import TestModeBanner from "@/components/layout/TestModeBanner";
import Sidebar from "@/components/layout/Sidebar";
import TopHeader from "@/components/layout/TopHeader";
import MainLayout from "@/components/layout/MainLayout";
import LoginPage from "@/pages/auth/LoginPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import InvoicesPage from "@/pages/invoices/InvoicesPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import AdminOnlyRoute from "@/components/AdminOnlyRoute";
import KsefConfigPage from "@/pages/ksef-config/KsefConfigPage"; // KROK 4 - aktywny entry point
import KsefInboxPage from "@/pages/ksef-inbox/KsefInboxPage";
import AiAgentsSettingsPage from "@/pages/ai-agents-settings/AiAgentsSettingsPage";
import DevToolsPage from "@/pages/dev-tools/DevToolsPage";
import BankStatementsPage from "@/pages/bank-statements/BankStatementsPage";
import BankAgentPage from "@/pages/bank-statements/BankAgentPage";
import InvoiceAgentPage from "@/pages/invoices/InvoiceAgentPage";
import PaymentPackagesPage from "@/pages/payment-packages/PaymentPackagesPage";
import CompanySettingsPage from "@/pages/company-settings/CompanySettingsPage";
import LockedPage from "@/pages/locked/LockedPage";
import PlaceholderPage from "@/components/shared/PlaceholderPage";
import ProtectedRoute from "@/components/shared/ProtectedRoute";
import PwaInstallPrompt from "@/pwa/PwaInstallPrompt";
import PwaUpdateToast from "@/pwa/PwaUpdateToast";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// App Router - Firebase Auth only (no legacy OAuth callback)
function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/locked" element={<LockedPage />} />
      
      {/* Web routes (desktop) */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout><DashboardPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute>
          <MainLayout><InvoicesPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/faktury/agent" element={
        <ProtectedRoute>
          <MainLayout><InvoiceAgentPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AdminOnlyRoute>
            <MainLayout><SettingsPage /></MainLayout>
          </AdminOnlyRoute>
        </ProtectedRoute>
      } />
      <Route path="/ksef-settings" element={<Navigate to="/ksef-config" replace />} />
      {/* Zakładka "Konfiguruj KSeF" w sidebarze — wrapper decyduje wizard vs tabsy */}
      <Route path="/ksef-config" element={
        <ProtectedRoute>
          <MainLayout><KsefConfigPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/ksef-inbox" element={
        <ProtectedRoute>
          <MainLayout><KsefInboxPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/ai-agents-settings" element={
        <ProtectedRoute>
          <MainLayout><AiAgentsSettingsPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/dev-tools" element={
        <ProtectedRoute>
          <MainLayout><DevToolsPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/wyciagi" element={
        <ProtectedRoute>
          <MainLayout><BankStatementsPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/wyciagi/agent" element={
        <ProtectedRoute>
          <MainLayout><BankAgentPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/paczki" element={
        <ProtectedRoute>
          <MainLayout><PaymentPackagesPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/profil" element={
        <ProtectedRoute>
          <MainLayout><CompanySettingsPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <PwaUpdateToast />
        <AppRouter />
        <PwaInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
